import dayjs from 'dayjs';
import type { Checkin } from 'server/dbx/checkins';
import { CHECKIN_REACTIONS, CheckinFeedback } from 'shared/checkin-feedback';
import { useIntl } from 'shared/intl/use-intl';

type Props = {
  checkins: Checkin[];
  /** Coach replies and reactions, by standup id. */
  feedback?: Record<UUID, CheckinFeedback>;
};

function NewChip() {
  const intl = useIntl();
  return (
    <span class="text-xs font-semibold uppercase tracking-wider text-white bg-indigo-600 rounded-full px-2 py-0.5">
      {intl('New')}
    </span>
  );
}

/** What the student's coaches said about one standup or summary. */
export function CoachFeedbackSection({ feedback }: { feedback: CheckinFeedback }) {
  const intl = useIntl();
  if (!feedback.reactions.length && !feedback.replies.length) {
    return null;
  }
  const reactions = CHECKIN_REACTIONS.map((r) => ({
    ...r,
    who: feedback.reactions.filter((x) => x.reaction === r.key),
  })).filter((r) => r.who.length);
  const hasNewReaction = feedback.reactions.some((r) => !r.studentSeenAt);

  return (
    <section class="flex flex-col gap-3 border-t border-gray-200 dark:border-gray-700 pt-3">
      <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">
        {intl('From your coaches')}
      </h3>
      {reactions.length > 0 && (
        <div class="flex flex-wrap items-center gap-2 text-sm">
          {reactions.map((r) => (
            <span
              key={r.key}
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700"
            >
              <span aria-label={r.label}>{r.emoji}</span>
              <span class="text-xs text-gray-600 dark:text-gray-400">
                {r.who.map((x) => x.coachName).join(', ')}
              </span>
            </span>
          ))}
          {hasNewReaction && <NewChip />}
        </div>
      )}
      {feedback.replies.map((reply) => (
        <div key={reply.id} class="flex flex-col gap-1 border-l-2 border-indigo-200 pl-3">
          <div class="flex flex-wrap items-center gap-2 text-sm">
            <strong class="text-gray-800 dark:text-gray-200">{reply.authorName}</strong>
            <span class="text-gray-500">
              {intl('Coach')} · {dayjs(reply.createdAt).format('ddd, MMM D, h:mm A')}
            </span>
            {reply.isEdited && (
              <span class="text-xs uppercase tracking-wider text-gray-500 border border-gray-300 rounded-full px-2 py-0.5">
                {intl('edited')}
              </span>
            )}
            {!reply.studentSeenAt && <NewChip />}
          </div>
          <p class="whitespace-pre-wrap text-gray-800 dark:text-gray-200">{reply.body}</p>
        </div>
      ))}
    </section>
  );
}

export function CheckinHistory({ checkins, feedback }: Props) {
  return (
    <div class="flex flex-col gap-6">
      {checkins.map((checkin) => (
        <div
          key={checkin.id}
          class="border border-gray-200 dark:border-gray-700 rounded-sm bg-white dark:bg-gray-800 p-4 flex flex-col gap-3"
        >
          <header class="flex items-center gap-2">
            <strong class="text-gray-800 dark:text-gray-200">
              {dayjs(checkin.checkinDate).format('dddd, MMM D, YYYY')}
            </strong>
            {checkin.isEdited && (
              <span class="text-xs uppercase tracking-wider text-gray-500 border border-gray-300 rounded-full px-2 py-0.5">
                edited
              </span>
            )}
          </header>
          <div class="flex flex-col gap-3">
            {checkin.answers.map((answer, i) => (
              <div key={i} class="flex flex-col">
                <p class="font-semibold">{answer.prompt}</p>
                <p class="whitespace-pre-wrap">{answer.answer}</p>
              </div>
            ))}
          </div>
          {feedback?.[checkin.id] && <CoachFeedbackSection feedback={feedback[checkin.id]} />}
        </div>
      ))}
    </div>
  );
}
